import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useAccessControlOptional } from '../context/AccessControlContext'
import { buildPermissionCode, matchInterfaceCodeForPath } from '../utils/accessControlUtils'

const PERMISSIVE_ACTION = () => true

export const useInterfacePermissions = (interfaceCode) => {
  const accessControl = useAccessControlOptional()
  // Outside AccessControlProvider (e.g. amicale space) grant access; gating is upstream.
  const canAction = accessControl?.canAction || PERMISSIVE_ACTION
  const isSuperAdmin = accessControl?.isSuperAdmin || false

  return useMemo(() => {
    if (!interfaceCode) {
      return {
        interfaceCode: '',
        canView: false,
        canCreate: false,
        canUpdate: false,
        canDelete: false,
        canExport: false,
        canConsult: false,
        canManage: false,
        hasWriteAccess: false,
      }
    }

    const canView = canAction(interfaceCode, 'view')
    const canCreate = canAction(interfaceCode, 'create')
    const canUpdate = canAction(interfaceCode, 'update')
    const canDelete = canAction(interfaceCode, 'delete')
    const canExport = canAction(interfaceCode, 'export')
    const canConsult = canAction(interfaceCode, 'consult')

    return {
      interfaceCode,
      isSuperAdmin,
      canView,
      canCreate,
      canUpdate,
      canDelete,
      canExport,
      canConsult,
      canManage: canUpdate,
      hasWriteAccess: canCreate || canUpdate || canDelete,
      can: (action) => canAction(interfaceCode, action),
    }
  }, [interfaceCode, canAction, isSuperAdmin])
}

export const useRouteInterfacePermissions = () => {
  const { pathname } = useLocation()
  const interfaceCode = matchInterfaceCodeForPath(pathname)
  return useInterfacePermissions(interfaceCode)
}

export const buildPermissionCheck = (canAction, interfaceCode) => (action) =>
  canAction(interfaceCode, action)

export default useInterfacePermissions
